import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { toast } from 'react-toastify';
import { hostnameFromUrl } from 'services/hostname';
import ActivityPricesPanel from './pricesPanel';
import {
    authConfig,
    fetchPurchaseOrderVolumes,
    iframeSnippetFor,
    isInstallationDone,
    isSiteInstalled,
    leadFormUrlFor,
    marketFor,
    PIXEL_SCRIPT_SNIPPET,
    plural,
    PurchaseOrderVolume,
    SITE_TOOLS,
    SiteTool,
    TEST_LEAD_EMAIL,
    TemplateOverview,
    timeAgo,
} from 'services/integrations';

/**
 * "Installation" tab of a universal integration (`/integrations/:id?tab=installation`):
 *   ② Installer — the Pixel and the form, both required, per-tool guides, live checks, and the final
 *     check by test lead sent from the seller's own site;
 *   ③ Vos prix — a minimum price per activity, then each page's activity checked one by one (leads
 *     already sell with the detected activity: never blocked, only pushed);
 * and, once everything is done, the per-site / per-activity state instead of the steps.
 * See notes/ux-integrations-vendeur.md §4.3, §4.4, §4.6.
 */

type Activity = { industry?: string; subIndustry?: string };

export interface InstallTemplate {
    _id: string;
    name: string;
    isUniversal?: boolean;
    landingPageUrls?: string[];
    confirmedSites?: string[];
    pageIndustryOverrides?: Record<string, Activity & { validated?: boolean }>;
    domainIndustryOverrides?: Record<string, Activity>;
    defaultIndustry?: Activity;
    // This integration's own price per activity, over the account price (services/integrationPricing.ts in the back).
    activityPrices?: { industry: string; subIndustry?: string; minimumPrice: number }[];
}


interface TestLead {
    createdAt: string;
    landingPage: string | null;
    industry: string | null;
    subIndustry: string | null;
    minimumPrice: number | null;
}

type Step = 'install' | 'prices' | 'pages';

/* ── Small presentational pieces (same palette as the rest of the integration screens) ── */

const Dot = ({ tone, pulse }: { tone: 'ok' | 'warn' | 'wait'; pulse?: boolean }) => (
    <span
        className={`mt-[6px] h-2 w-2 shrink-0 rounded-full ${tone === 'ok' ? 'bg-[#1E9E52]' : tone === 'warn' ? 'bg-[#D9A100]' : 'bg-[#C9BFAE]'} ${
            pulse ? 'animate-pulse' : ''
        }`}
    />
);

const btnPrimary =
    'rounded-[10px] bg-[#241C5C] px-4 py-2.5 font-hanken text-[13.5px] font-semibold text-white transition-colors hover:bg-[#332a75] disabled:cursor-not-allowed disabled:opacity-50';
const btnPrimarySm =
    'rounded-[9px] bg-[#241C5C] px-3 py-[7px] font-hanken text-[12.5px] font-semibold text-white transition-colors hover:bg-[#332a75] disabled:opacity-50';
const btnGhost =
    'rounded-[10px] border border-[#DDD5C9] bg-white px-4 py-2.5 font-hanken text-[13.5px] font-semibold text-[#3B352C] transition-colors hover:border-[#241C5C] disabled:opacity-50';
const btnGhostSm =
    'rounded-[9px] border border-[#DDD5C9] bg-white px-3 py-[7px] font-hanken text-[12.5px] font-semibold text-[#3B352C] transition-colors hover:border-[#241C5C] disabled:opacity-50';
const btnLink = 'font-hanken text-[13px] font-semibold text-[#241C5C] hover:underline';
const chipOk = 'rounded-full bg-[#D9F0DE] px-2.5 py-[3px] font-hanken text-[12px] font-semibold text-[#146334]';
const chipWarn = 'rounded-full bg-[#FCF2D4] px-2.5 py-[3px] font-hanken text-[12px] font-semibold text-[#7A5A05]';
const card = 'rounded-[18px] border border-[#EDE7DE] bg-white';

const Snippet = ({ code, onCopy, copied }: { code: string; onCopy: () => void; copied: boolean }) => (
    <div className="flex items-center gap-3.5 rounded-[13px] border border-[#EDE7DE] bg-[#F7F5F1] px-[15px] py-[12px]">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap font-jbmono text-[12px] text-[#2E2A24]">{code}</code>
        <button onClick={onCopy} className={btnPrimarySm}>
            {copied ? 'Copié ✓' : 'Copier'}
        </button>
    </div>
);

const Num = ({ n }: { n: number }) => (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#241C5C] font-hanken text-[12.5px] font-bold text-white">{n}</span>
);

/* ── Polling helper: runs `fn` now and every `ms` while `active`, pauses when the tab is hidden ── */

const usePoll = (fn: () => void, active: boolean, ms = 5000) => {
    const saved = useRef(fn);
    saved.current = fn;
    useEffect(() => {
        if (!active) return;
        const tick = () => {
            if (!document.hidden) saved.current();
        };
        tick();
        const id = window.setInterval(tick, ms);
        return () => window.clearInterval(id);
    }, [active, ms]);
};

const pagePath = (url: string) => url.replace(/^https?:\/\//, '');

const InstallationTab = ({
    template,
    onTemplateChange,
    user,
    pixelRequired,
    overview,
    refreshOverview,
    onOpenPages,
}: {
    template: InstallTemplate;
    onTemplateChange: (t: any) => void;
    user: any;
    pixelRequired: boolean;
    overview: TemplateOverview | null;
    refreshOverview: () => void;
    // Opens the Pages tab, with its URL params (filter, activity, review, add).
    onOpenPages: (params: Record<string, string>) => void;
}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const requestedStep = searchParams.get('step') as Step | null;
    const done = !!overview && isInstallationDone(overview, pixelRequired);
    const [review, setReview] = useState(!!requestedStep);
    const firstUnfinished: Step =
        !overview || overview.sites.length === 0 || overview.sites.some((s) => !isSiteInstalled(s, pixelRequired)) ? 'install' : 'prices';
    const step: Step = requestedStep === 'install' || requestedStep === 'prices' || requestedStep === 'pages' ? requestedStep : firstUnfinished;
    const showStatus = done && !review;

    const goStep = (next: Step) => {
        setReview(true);
        const params = new URLSearchParams(searchParams);
        params.set('tab', 'installation');
        params.set('step', next);
        setSearchParams(params, { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const [copied, setCopied] = useState<string | null>(null);
    const copy = (text: string, key: string) => {
        navigator.clipboard?.writeText(text).catch(() => {});
        setCopied(key);
        window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
    };

    /* ── Step ② Installer ─────────────────────────────────────────────────────────────────── */

    // The site being installed: ?site= (a card's "Terminer"), else the first site still missing
    // something, else the seller's own website — a new site ("Ajouter un site") starts empty.
    const initialSite = useMemo(() => {
        const fromParam = searchParams.get('site');
        if (fromParam) return fromParam;
        if (searchParams.get('new') === '1') return '';
        const pending = overview?.sites.find((s) => !isSiteInstalled(s, pixelRequired));
        if (pending) return pending.hostname;
        if (overview?.sites[0]) return overview.sites[0].hostname;
        return hostnameFromUrl(user?.website || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overview === null, user?.website]);

    const [siteInput, setSiteInput] = useState('');
    const [host, setHost] = useState('');
    useEffect(() => {
        if (!siteInput && initialSite) {
            setSiteInput(initialSite);
            setHost(hostnameFromUrl(initialSite));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSite]);

    const [tool, setTool] = useState<SiteTool>('wordpress');
    const toolGuide = SITE_TOOLS.find((t) => t.key === tool)!;

    const [pixel, setPixel] = useState<{ host: string; detected: boolean } | null>(null);
    const [iframe, setIframe] = useState<{ host: string; detected: boolean; views: number } | null>(null);
    const pixelOk = !pixelRequired || (pixel?.host === host && pixel.detected);
    const iframeOk = iframe?.host === host && iframe.detected;

    usePoll(
        () => {
            const h = host;
            axios
                .get(`${CONFIG.apiUrl}/leadrs-pixel/status`, { params: { url: h } })
                .then((res) => setPixel({ host: h, detected: !!res.data?.detected }))
                .catch(() => setPixel({ host: h, detected: false }));
        },
        !!host && pixelRequired && !(pixel?.host === host && pixel.detected) && !showStatus
    );

    usePoll(
        () => {
            const h = host;
            axios
                .get(`${CONFIG.apiUrl}/form-template/${template._id}/iframe-status`, { ...authConfig(), params: { hostname: h } })
                .then((res) => setIframe({ host: h, detected: !!res.data?.detected, views: res.data?.views || 0 }))
                .catch(() => setIframe({ host: h, detected: false, views: 0 }));
        },
        !!host && !iframeOk && !showStatus
    );

    // Once the site answers, discover its pages from the sitemap in the background (once per host),
    // so they're already listed at "Vos prix". Gated on a real detection: the crawl seeds page rows.
    const explored = useRef<Set<string>>(new Set());
    const [pagesScan, setPagesScan] = useState<'idle' | 'running' | { added: number }>('idle');
    useEffect(() => {
        if (!host || !pixelOk || !(pixelRequired || iframeOk) || explored.current.has(host)) return;
        explored.current.add(host);
        setPagesScan('running');
        axios
            .post(`${CONFIG.apiUrl}/form-template/${template._id}/explore-sitemap`, { siteUrl: host }, authConfig())
            .then((res) => {
                if (res.data?.template) onTemplateChange(res.data.template);
                setPagesScan({ added: res.data?.added?.length || 0 });
                refreshOverview();
            })
            .catch(() => setPagesScan('idle'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [host, pixelOk, iframeOk, pixelRequired]);

    // Refresh the overview once both checks pass, so the rail and the cards follow.
    useEffect(() => {
        if (host && pixelOk && iframeOk) refreshOverview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [host, pixelOk, iframeOk]);

    const pagesOnHost = (template.landingPageUrls || []).filter((u) => hostnameFromUrl(u) === host).length;

    // Final check: the seller fills the form on their own site with testing@leadrs.co.
    const [testLead, setTestLead] = useState<TestLead | null>(null);
    const testStartedAt = useRef(Date.now());
    usePoll(
        () => {
            axios
                .get(`${CONFIG.apiUrl}/form-template/${template._id}/last-test-lead`, authConfig())
                .then((res) => setTestLead(res.data?.lead || null))
                .catch(() => {});
        },
        !showStatus && step === 'install' && !!iframeOk && !(testLead && new Date(testLead.createdAt).getTime() >= testStartedAt.current)
    );

    const [volumes, setVolumes] = useState<PurchaseOrderVolume[]>([]);
    useEffect(() => {
        fetchPurchaseOrderVolumes()
            .then(setVolumes)
            .catch(() => {});
    }, []);

    const [showPreview, setShowPreview] = useState(false);

    const iframeUrl = leadFormUrlFor(template._id);

    /* ── Steps ③ Vos prix and ④ Vos pages: read from the overview; edited in their own tabs ── */

    const activities = overview?.activities || [];
    const unpriced = activities.filter((a) => a.minimumPrice == null).length;
    const siteStats = overview?.sites || [];
    const totalPages = siteStats.reduce((n, s) => n + s.pages, 0);
    const verifiedPages = siteStats.reduce((n, s) => n + s.verifiedPages, 0);
    const unclassifiedPages = siteStats.reduce((n, s) => n + s.unclassifiedPages, 0);
    const toCheckPages = Math.max(0, totalPages - verifiedPages - unclassifiedPages);

    /* ── Rendering ────────────────────────────────────────────────────────────────────────── */

    if (showStatus && overview) {
        return (
            <div className="mt-6 flex flex-col gap-5">
                <div className="flex flex-wrap items-center gap-3.5 rounded-[14px] border border-[#CDEAD8] bg-[#F2FAF4] px-[18px] py-3.5">
                    <Dot tone="ok" />
                    <p className="min-w-[220px] flex-1 font-hanken text-[14px] text-[#3B352C]">
                        <strong>Vos sites sont en ligne et vos leads se vendent.</strong>{' '}
                        {verifiedPages < totalPages && (
                            <span className="text-[#6B6357]">Il reste {plural(totalPages - verifiedPages, 'page à vérifier', 'pages à vérifier')}.</span>
                        )}
                    </p>
                    {verifiedPages < totalPages && (
                        <button className={btnPrimarySm} onClick={() => onOpenPages({ review: '1' })}>
                            Vérifier les pages →
                        </button>
                    )}
                </div>

                <div className={`${card} overflow-hidden`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#F1ECE4] px-5 py-4">
                        <h3 className="font-newsreader text-[20px] font-medium text-[#14110E]">État par site</h3>
                        <button
                            className={btnGhostSm}
                            onClick={() => {
                                setSiteInput('');
                                setHost('');
                                goStep('install');
                            }}
                        >
                            Ajouter un site
                        </button>
                    </div>
                    {overview.sites.map((s) => (
                        <div key={s.hostname} className="grid grid-cols-2 items-center gap-2 border-b border-[#F1ECE4] px-5 py-3 font-hanken text-[13.5px] last:border-0 md:grid-cols-5">
                            <span className="col-span-2 font-semibold md:col-span-1">{s.hostname}</span>
                            <span className="flex gap-3 text-[13px]">
                                {pixelRequired && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Dot tone={s.pixelDetected ? 'ok' : 'warn'} />
                                        Suivi
                                    </span>
                                )}
                                <span className="inline-flex items-center gap-1.5">
                                    <Dot tone={s.iframeViews > 0 ? 'ok' : 'warn'} />
                                    Formulaire
                                </span>
                            </span>
                            <span>{s.pages ? `${s.verifiedPages}/${s.pages} vérifiées` : '—'}</span>
                            <span>{s.leads30 ? `${s.leads30}` : '—'}</span>
                            <span className="text-[#6B6357]">{timeAgo(s.lastLeadAt)}</span>
                        </div>
                    ))}
                </div>

                <div className={`${card} overflow-hidden`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#F1ECE4] px-5 py-4">
                        <h3 className="font-newsreader text-[20px] font-medium text-[#14110E]">Vos activités</h3>
                        <button className={btnGhostSm} onClick={() => goStep('prices')}>
                            Modifier les prix
                        </button>
                    </div>
                    {overview.activities.map((a) => {
                        const market = marketFor(volumes, a.subIndustry, user);
                        return (
                            <div
                                key={`${a.industry}|${a.subIndustry}`}
                                className="grid grid-cols-2 items-center gap-2 border-b border-[#F1ECE4] px-5 py-3 font-hanken text-[13.5px] last:border-0 md:grid-cols-5"
                            >
                                <span className="col-span-2 font-semibold md:col-span-1">{a.subIndustry || a.industry}</span>
                                <span>
                                    {a.minimumPrice != null ? `${a.minimumPrice} € minimum` : '—'}
                                    {a.priceSource && (
                                        <span className="block text-[11.5px] text-[#8A8172]">
                                            {a.priceSource === 'account' ? 'prix du compte' : `prix propre${a.accountPrice != null ? ` (compte : ${a.accountPrice} €)` : ''}`}
                                        </span>
                                    )}
                                </span>
                                <span>{plural(a.pages, 'page', 'pages')}</span>
                                <span>{plural(market.orders, "ordre d'achat actif", "ordres d'achat actifs")}</span>
                                <span>{a.minimumPrice != null ? <span className={chipOk}>Prix fixé</span> : <span className={chipWarn}>Prix à fixer</span>}</span>
                            </div>
                        );
                    })}
                </div>

                <button className={`${btnLink} self-start`} onClick={() => goStep('install')}>
                    Revoir les étapes d'installation
                </button>
            </div>
        );
    }

    const installState: 'done' | 'warn' | 'current' = overview && overview.sites.length > 0 && overview.sites.every((s) => isSiteInstalled(s, pixelRequired)) ? 'done' : overview?.sites.length ? 'warn' : 'current';
    const pricesState: 'done' | 'warn' | 'todo' = activities.length === 0 ? 'todo' : unpriced > 0 ? 'warn' : 'done';
    const pagesState: 'done' | 'warn' | 'todo' = totalPages === 0 ? 'todo' : unclassifiedPages + toCheckPages > 0 ? 'warn' : 'done';
    const missingSites = (overview?.sites || []).filter((s) => !isSiteInstalled(s, pixelRequired)).map((s) => s.hostname);

    const RailItem = ({ n, title, sub, state, target }: { n: number; title: string; sub: string; state: 'done' | 'warn' | 'current' | 'todo'; target?: Step }) => (
        <button
            onClick={() => target && goStep(target)}
            disabled={!target}
            aria-current={target === step ? 'step' : undefined}
            className={`flex w-full items-start gap-3 rounded-[12px] px-3 py-3 text-left transition-colors ${
                target === step ? 'bg-[#FAF3E8] shadow-[inset_0_0_0_1.5px_#E6D6BC]' : target ? 'hover:bg-[#FAF8F4]' : ''
            }`}
        >
            <span
                className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full font-hanken text-[12px] font-bold ${
                    state === 'done'
                        ? 'bg-[#D9F0DE] text-[#146334]'
                        : state === 'warn'
                        ? 'bg-[#FCF2D4] text-[#7A5A05]'
                        : state === 'current'
                        ? 'bg-[#241C5C] text-white'
                        : 'bg-[#F1ECE4] text-[#9A9184]'
                }`}
            >
                {state === 'done' ? '✓' : state === 'warn' ? '!' : n}
            </span>
            <span className="min-w-0">
                <span className="block font-hanken text-[14.5px] font-semibold text-[#14110E]">{title}</span>
                <span className="mt-0.5 block font-hanken text-[12.5px] leading-snug text-[#8A8172]">{sub}</span>
            </span>
        </button>
    );

    return (
        <div className="mt-6 flex flex-wrap items-start gap-6">
            <nav className={`${card} w-full p-2.5 lg:sticky lg:top-6 lg:max-w-[280px] lg:flex-[1_1_240px]`} aria-label="Étapes">
                <RailItem n={1} title="Votre situation" sub="J'ai déjà un site" state="done" />
                <RailItem
                    n={2}
                    title="Installer"
                    sub={
                        installState === 'done'
                            ? plural(overview?.sites.length || 0, 'site en ligne', 'sites en ligne')
                            : missingSites.length
                            ? `À terminer sur ${missingSites.length > 1 ? `${missingSites.length} sites` : missingSites[0]}`
                            : pixelRequired
                            ? 'Code de suivi + formulaire'
                            : 'Formulaire dans la page'
                    }
                    state={installState}
                    target="install"
                />
                <RailItem
                    n={3}
                    title="Vos prix"
                    sub={
                        activities.length === 0
                            ? 'Selon les activités de vos pages'
                            : unpriced
                            ? plural(unpriced, 'activité sans prix', 'activités sans prix')
                            : 'Toutes les activités ont un prix'
                    }
                    state={pricesState}
                    target="prices"
                />
                <RailItem
                    n={4}
                    title="Vos pages"
                    sub={
                        totalPages === 0
                            ? 'Ce que vend chaque page'
                            : unclassifiedPages + toCheckPages
                            ? plural(unclassifiedPages + toCheckPages, 'page à vérifier', 'pages à vérifier')
                            : plural(totalPages, 'page vérifiée', 'pages vérifiées')
                    }
                    state={pagesState}
                    target="pages"
                />
            </nav>

            <section className="flex min-w-0 flex-[999_1_460px] flex-col gap-5">
                {step === 'install' && (
                    <div className={`${card} flex flex-col gap-6 p-7`}>
                        <div>
                            <p className="font-hanken text-[12px] font-bold uppercase tracking-[0.09em] text-[#A79E90]">Étape 2</p>
                            <h2 className="mt-2 font-newsreader text-[28px] font-medium tracking-[-0.015em] text-[#14110E]">Installez Leadrs sur votre site</h2>
                            <p className="mt-1.5 max-w-[640px] font-hanken text-[14.5px] leading-relaxed text-[#6B6357]">
                                {pixelRequired
                                    ? "Deux codes à coller, tous les deux nécessaires : le code de suivi reconnaît l'activité de chaque page, le formulaire recueille les leads."
                                    : 'Collez le formulaire là où vos visiteurs doivent le remplir.'}
                            </p>
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                setHost(hostnameFromUrl(siteInput));
                            }}
                            className="flex flex-col gap-1.5"
                        >
                            <label htmlFor="install-site" className="font-hanken text-[13.5px] font-semibold text-[#3B352C]">
                                Votre site
                            </label>
                            <div className="flex flex-wrap gap-2.5">
                                <input
                                    id="install-site"
                                    value={siteInput}
                                    onChange={(e) => setSiteInput(e.target.value)}
                                    placeholder="exemple.fr"
                                    inputMode="url"
                                    className="min-w-[200px] flex-1 rounded-[10px] border border-[#DDD5C9] bg-white px-[13px] py-[10px] font-hanken text-[14px] outline-none focus:border-[#241C5C]"
                                />
                                <button type="submit" disabled={!hostnameFromUrl(siteInput)} className={btnGhost}>
                                    {host && host === hostnameFromUrl(siteInput) ? 'Revérifier' : 'Vérifier'}
                                </button>
                            </div>
                        </form>

                        <div className="flex flex-col gap-2.5">
                            <span className="font-hanken text-[13.5px] font-semibold text-[#3B352C]">Votre site est fait avec</span>
                            <div className="flex flex-wrap gap-2">
                                {SITE_TOOLS.map((t) => (
                                    <button
                                        key={t.key}
                                        onClick={() => setTool(t.key)}
                                        aria-pressed={tool === t.key}
                                        className={`rounded-full border px-[13px] py-1.5 font-hanken text-[13px] font-semibold transition-colors ${
                                            tool === t.key ? 'border-[#241C5C] bg-[#241C5C] text-white' : 'border-[#DDD5C9] bg-white text-[#3B352C] hover:border-[#241C5C]'
                                        }`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {pixelRequired && (
                            <div className="flex flex-col gap-2.5">
                                <div className="flex flex-wrap items-center gap-2.5">
                                    <Num n={1} />
                                    <strong className="font-hanken text-[14.5px]">Code de suivi</strong>
                                    <span className="font-hanken text-[13px] text-[#8A8172]">à coller une fois, dans l'en-tête de toutes les pages</span>
                                </div>
                                <Snippet code={PIXEL_SCRIPT_SNIPPET} copied={copied === 'pixel'} onCopy={() => copy(PIXEL_SCRIPT_SNIPPET, 'pixel')} />
                                <div className="rounded-[13px] border border-[#EDE7DE] bg-[#FAF8F4] px-4 py-3 font-hanken text-[13.5px] text-[#3B352C]">
                                    <strong>Avec {toolGuide.label}</strong>
                                    <ol className="mt-1.5 list-decimal pl-5">
                                        {toolGuide.pixel.map((line) => (
                                            <li key={line} className="my-0.5">
                                                {line}
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col gap-2.5">
                            <div className="flex flex-wrap items-center gap-2.5">
                                <Num n={pixelRequired ? 2 : 1} />
                                <strong className="font-hanken text-[14.5px]">Formulaire</strong>
                                <span className="font-hanken text-[13px] text-[#8A8172]">à coller là où vos visiteurs doivent le remplir</span>
                            </div>
                            <Snippet code={iframeSnippetFor(template._id)} copied={copied === 'iframe'} onCopy={() => copy(iframeSnippetFor(template._id), 'iframe')} />
                            <div className="rounded-[13px] border border-[#EDE7DE] bg-[#FAF8F4] px-4 py-3 font-hanken text-[13.5px] text-[#3B352C]">
                                <strong>Avec {toolGuide.label}</strong>
                                <ol className="mt-1.5 list-decimal pl-5">
                                    {toolGuide.form.map((line) => (
                                        <li key={line} className="my-0.5">
                                            {pixelRequired ? line : line.replace('code ②', 'code ①')}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                            <button className={`${btnLink} self-start`} onClick={() => setShowPreview((v) => !v)}>
                                {showPreview ? "Masquer l'aperçu" : "Voir l'aperçu du formulaire"}
                            </button>
                            {showPreview && (
                                <div className="rounded-[16px] border border-dashed border-[#DDD5C9] bg-[#FAF8F4] p-3">
                                    <iframe title="Aperçu du formulaire" src={`${iframeUrl}&preview=1`} className="h-[600px] w-full rounded-[12px] border-0 bg-white" />
                                    <p className="mt-2 text-center font-hanken text-[12.5px] text-[#8A8172]">
                                        Sur votre site, les champs s'adaptent à l'activité de chaque page. L'apparence se règle dans l'onglet Apparence.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col gap-2.5">
                            <strong className="font-hanken text-[14.5px]">Vérification en direct</strong>
                            <div className="overflow-hidden rounded-[14px] border border-[#EDE7DE]">
                                {!host ? (
                                    <div className="flex gap-3 bg-white px-4 py-3.5">
                                        <Dot tone="wait" />
                                        <p className="font-hanken text-[13.5px] text-[#6B6357]">Indiquez l'adresse de votre site pour lancer la vérification.</p>
                                    </div>
                                ) : (
                                    <>
                                        {pixelRequired && (
                                            <div className="flex gap-3 border-b border-[#F1ECE4] bg-white px-4 py-3.5">
                                                <Dot tone={pixelOk ? 'ok' : 'wait'} pulse={!pixelOk} />
                                                <div className="font-hanken text-[13.5px]">
                                                    <p>
                                                        <strong>{pixelOk ? 'Code de suivi détecté' : 'Code de suivi'}</strong> sur {host}
                                                    </p>
                                                    {!pixelOk && (
                                                        <p className="text-[13px] text-[#8A8172]">
                                                            Publiez votre site, puis ouvrez une de ses pages : on le détecte en quelques secondes.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <div
                                            className={`flex gap-3 border-b border-[#F1ECE4] px-4 py-3.5 ${
                                                pixelRequired && pixelOk && !iframeOk ? 'bg-[#FCF9EF]' : 'bg-white'
                                            }`}
                                        >
                                            <Dot tone={iframeOk ? 'ok' : pixelRequired && pixelOk ? 'warn' : 'wait'} pulse={!iframeOk && !(pixelRequired && pixelOk)} />
                                            <div className="font-hanken text-[13.5px]">
                                                {iframeOk ? (
                                                    <p>
                                                        <strong>Formulaire affiché</strong> sur {host} · {plural(iframe?.views || 0, 'vue', 'vues')}
                                                    </p>
                                                ) : pixelRequired && pixelOk ? (
                                                    <>
                                                        <p>
                                                            <strong>Code de suivi trouvé, mais pas encore le formulaire.</strong>
                                                        </p>
                                                        <p className="text-[#6B6357]">
                                                            Collez le code ② sur au moins une page de {host}, puis ouvrez cette page. Sans formulaire, aucun
                                                            lead ne peut arriver.
                                                        </p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p>
                                                            <strong>Formulaire</strong> sur {host}
                                                        </p>
                                                        <p className="text-[13px] text-[#8A8172]">On le détecte dès qu'une page qui le contient est ouverte.</p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-3 bg-white px-4 py-3.5">
                                            <Dot tone={pagesOnHost > 0 ? 'ok' : 'wait'} pulse={pagesScan === 'running'} />
                                            <p className="font-hanken text-[13.5px]">
                                                {pagesOnHost > 0 ? (
                                                    <>
                                                        <strong>{plural(pagesOnHost, 'page trouvée', 'pages trouvées')}</strong> sur {host}
                                                    </>
                                                ) : pagesScan === 'running' ? (
                                                    'Recherche des pages de votre site…'
                                                ) : (
                                                    <span className="text-[#8A8172]">Les pages du site apparaîtront dès que le site répond.</span>
                                                )}
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {testLead && iframeOk ? (
                            <div className="rounded-[14px] border border-[#CDEAD8] bg-[#F2FAF4] p-[18px] font-hanken">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Dot tone="ok" />
                                    <strong className="text-[14.5px]">Lead de test reçu</strong>
                                    <span className="text-[13px] text-[#6B6357]">
                                        {testLead.landingPage ? `depuis ${pagePath(testLead.landingPage)} ` : ''}
                                        {timeAgo(testLead.createdAt)}
                                    </span>
                                </div>
                                {(() => {
                                    const market = marketFor(volumes, testLead.subIndustry, user);
                                    const sellable = !!testLead.industry && testLead.minimumPrice != null;
                                    return (
                                        <>
                                            <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                                                <div className="rounded-[11px] border border-[#CDEAD8] bg-white px-3 py-2.5">
                                                    <span className="text-[12.5px] text-[#8A8172]">Activité détectée</span>
                                                    <b className="block text-[15px]">{testLead.subIndustry || testLead.industry || 'Non détectée'}</b>
                                                </div>
                                                <div className="rounded-[11px] border border-[#CDEAD8] bg-white px-3 py-2.5">
                                                    <span className="text-[12.5px] text-[#8A8172]">Votre prix minimum</span>
                                                    <b className="block text-[15px]">{testLead.minimumPrice != null ? `${testLead.minimumPrice} €` : 'Pas encore fixé'}</b>
                                                </div>
                                                <div className="rounded-[11px] border border-[#CDEAD8] bg-white px-3 py-2.5">
                                                    <span className="text-[12.5px] text-[#8A8172]">Ordres d'achat actifs</span>
                                                    <b className="block text-[15px]">{market.orders}</b>
                                                </div>
                                            </div>
                                            <p className="mt-3 text-[13.5px]">
                                                {sellable && market.orders > 0 ? (
                                                    <strong className="text-[#146334]">Un vrai lead sur cette page serait vendu.</strong>
                                                ) : !testLead.industry ? (
                                                    <>
                                                        <strong className="text-[#7A5A05]">L'activité de cette page n'est pas encore connue.</strong>{' '}
                                                        <button className={btnLink} onClick={() => onOpenPages({ filter: 'unclassified' })}>
                                                            Indiquer l'activité
                                                        </button>
                                                    </>
                                                ) : testLead.minimumPrice == null ? (
                                                    <>
                                                        <strong className="text-[#7A5A05]">Il manque votre prix minimum pour cette activité.</strong>{' '}
                                                        <button className={btnLink} onClick={() => goStep('prices')}>
                                                            Fixer le prix
                                                        </button>
                                                    </>
                                                ) : (
                                                    <strong className="text-[#7A5A05]">Aucun acheteur actif pour le moment : un vrai lead serait mis en attente.</strong>
                                                )}{' '}
                                                <span className="text-[#6B6357]">
                                                    C'est normal que ce lead apparaisse comme « test » dans Mes leads : un lead de test n'est jamais vendu.
                                                </span>
                                            </p>
                                        </>
                                    );
                                })()}
                            </div>
                        ) : (
                            <div className="rounded-[14px] border border-[#EDE7DE] bg-white p-[18px] font-hanken">
                                <strong className="text-[14.5px]">Validez avec un lead de test</strong>
                                <p className="mt-1 text-[13.5px] text-[#6B6357]">
                                    {iframeOk
                                        ? 'On attend votre lead de test…'
                                        : 'Possible dès que le formulaire est affiché sur votre site.'}
                                </p>
                                <ol className="mt-2 list-decimal pl-5 text-[13.5px] text-[#3B352C]">
                                    <li>Ouvrez une page de votre site où se trouve le formulaire{host ? ` (${host})` : ''}.</li>
                                    <li>
                                        Remplissez-le avec l'e-mail <code className="font-jbmono text-[12.5px]">{TEST_LEAD_EMAIL}</code>{' '}
                                        <button className={btnLink} onClick={() => copy(TEST_LEAD_EMAIL, 'test-email')}>
                                            {copied === 'test-email' ? 'Copié ✓' : 'Copier'}
                                        </button>
                                    </li>
                                </ol>
                                <p className="mt-2 text-[12.5px] text-[#8A8172]">Un lead de test n'est jamais vendu ni facturé.</p>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3 border-t border-[#F1ECE4] pt-5">
                            <button className={btnPrimary} onClick={() => goStep('prices')}>
                                Continuer vers vos prix →
                            </button>
                            {!(pixelOk && iframeOk) && (
                                <>
                                    <button className={btnGhost} onClick={() => goStep('prices')}>
                                        Je publierai plus tard
                                    </button>
                                    <span className="font-hanken text-[13px] text-[#8A8172]">On continue de vérifier en arrière-plan.</span>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {step === 'prices' && (
                    <>
                        <div className={`${card} flex flex-col gap-2 p-7`}>
                            <p className="font-hanken text-[12px] font-bold uppercase tracking-[0.09em] text-[#A79E90]">Étape 3</p>
                            <h2 className="font-newsreader text-[28px] font-medium tracking-[-0.015em] text-[#14110E]">Vos prix</h2>
                            <p className="max-w-[680px] font-hanken text-[14.5px] leading-relaxed text-[#6B6357]">
                                Le prix minimum par lead de chaque activité vendue par ce formulaire. Celui de Mes prix est proposé par défaut ;
                                vous pouvez en fixer un propre à ce formulaire. Vous le retrouvez ensuite dans l'onglet Prix.
                            </p>
                        </div>
                        <ActivityPricesPanel
                            templateId={template._id}
                            overview={overview}
                            refreshOverview={refreshOverview}
                            onTemplateChange={onTemplateChange}
                            user={user}
                            onShowPages={(a) => onOpenPages({ activity: `${a.industry}|${a.subIndustry}` })}
                        />
                        <div className="flex flex-wrap items-center gap-3">
                            <button className={btnPrimary} onClick={() => goStep('pages')}>
                                Continuer vers vos pages →
                            </button>
                        </div>
                    </>
                )}

                {step === 'pages' && (
                    <div className={`${card} flex flex-col gap-5 p-7`}>
                        <div>
                            <p className="font-hanken text-[12px] font-bold uppercase tracking-[0.09em] text-[#A79E90]">Étape 4</p>
                            <h2 className="mt-2 font-newsreader text-[28px] font-medium tracking-[-0.015em] text-[#14110E]">Vos pages</h2>
                            <p className="mt-1.5 max-w-[680px] font-hanken text-[14.5px] leading-relaxed text-[#6B6357]">
                                Chaque page vend une activité, détectée automatiquement. Vos leads se vendent déjà avec elle : vérifiez-la pour
                                être sûr de vendre au bon acheteur. Tout se gère ensuite dans l'onglet Pages.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                            {[
                                { label: 'Pages', value: totalPages, tone: 'text-[#14110E]' },
                                { label: 'Vérifiées', value: verifiedPages, tone: 'text-[#146334]' },
                                { label: 'À vérifier', value: toCheckPages, tone: 'text-[#7A5A05]' },
                                { label: 'Sans activité', value: unclassifiedPages, tone: 'text-[#A8332B]' },
                            ].map((k) => (
                                <div key={k.label} className="rounded-[12px] border border-[#EDE7DE] bg-[#FAF8F4] px-3.5 py-2.5 font-hanken">
                                    <span className="text-[12px] text-[#8A8172]">{k.label}</span>
                                    <b className={`block font-newsreader text-[24px] font-medium ${k.tone}`}>{k.value}</b>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-2.5">
                            {unclassifiedPages + toCheckPages > 0 && (
                                <button className={btnPrimary} onClick={() => onOpenPages({ review: '1' })}>
                                    Vérifier une à une →
                                </button>
                            )}
                            <button className={btnGhost} onClick={() => onOpenPages({ add: '1' })}>
                                + Ajouter une page
                            </button>
                            <button className={btnLink} onClick={() => onOpenPages({})}>
                                Voir toutes mes pages
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default InstallationTab;
